import React, { useState } from 'react';
import { Box, Typography, Button, TextField, useMediaQuery, Alert } from '@mui/material';
import Header from './Header';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { endpoint } from '../../resource/Constant';
import { useNavigate } from 'react-router-dom';
 
const LinkedInPost = () => {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [openaiPost, setOpenaiPost] = useState('');
  const [geminiPost, setGeminiPost] = useState('');
  const [customPost, setCustomPost] = useState('');
  const [error, setError] = useState(null);
  const [sentiment, setSentiment] = useState('');

  const user_email = localStorage.getItem('user_email');
  const token = localStorage.getItem('linkedin_token');
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  const handlePost = async () => {
    if (!topic) {
      setError('Please enter a topic');
      return;
    }

    setLoading(true);
    setError(null);
    setSentiment('');

      axios
      .get(
        `${endpoint}/generate-post?topic=${encodeURIComponent(topic)}&user_email=${encodeURIComponent(user_email)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      .then((response) => {
        setOpenaiPost(response.data.openai_response || '');
        setGeminiPost(response.data.gemini_response || '');
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(error?.response?.data?.message || "Internal error")
        if (error?.status === 401) {
          localStorage.removeItem('linkedin_token')
          setTimeout(() => {
            navigate('/linkedinpost/signin')
          }, 1000);
        }
      })
  };

  const handleSentimentCheck = async () => {
  setError(null);

  if (!openaiPost?.trim() || !geminiPost?.trim()) {
    setError("Please generate both OpenAI and Gemini posts before analyzing sentiment.");
    return;
  }

    axios
    .post(
      `${endpoint}/compare-sentiment?user_email=${encodeURIComponent(user_email)}`,
      {
        openai_post: openaiPost,
        gemini_post: geminiPost,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    .then((res) => {
      const positivePost =
      res.data.more_positive === 'A' ? openaiPost : geminiPost;
      setCustomPost(positivePost);
      setSentiment(
        `Post ${res.data.more_positive} is more positive. Reason: ${res.data.reason}`
      );
      setError(null); // Clear error on success
    })
    .catch((error) => {
      setError(error?.response?.data?.message || "Sentiment analysis failed. Please try again.")
      if (error?.status === 401) {
          localStorage.removeItem('linkedin_token')
          setTimeout(() => {
            navigate('/linkedinpost/signin')
          }, 1000);
        }
    }) 
};

  

  return (
  <>
    <Header />
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f4f6f8',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: 4,
        marginTop: '80px' // Adjusted for spacing below header
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 800 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}
        >
          Generate Your LinkedIn Post
        </Typography>

        <TextField
          label="Enter Topic"
          variant="outlined"
          fullWidth
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          sx={{ mb: 2, borderRadius: 2, px: 4, py: 1.5, textTransform: 'none' }}
          onClick={handlePost}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate Content'}
        </Button>

        {/* Guidance for user after generation */}
        {!loading && (openaiPost || geminiPost) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            ✅ Click on a generated post below to select it. Then, you can edit, analyze sentiment, or post to LinkedIn!
          </Alert>
        )}

      
{error && (
  <Alert severity="error" sx={{ mb: 2 }}>
    😞 Oops! {error}
  </Alert>
)}


        {openaiPost && (
          <Box
            sx={{ mb: 4, cursor: 'pointer', '&:hover': { backgroundColor: '#eef2f5' }, p: 2, borderRadius: 2 }}
            onClick={() => setCustomPost(openaiPost)}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              💡 Innovator AI:
            </Typography>
            <ReactMarkdown>{openaiPost}</ReactMarkdown>
          </Box>
        )}

        {geminiPost && (
          <Box
            sx={{ mb: 4, cursor: 'pointer', '&:hover': { backgroundColor: '#eef2f5' }, p: 2, borderRadius: 2 }}
            onClick={() => setCustomPost(geminiPost)}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              🚀 Visionary AI:
            </Typography>
            <ReactMarkdown>{geminiPost}</ReactMarkdown>
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            ✍️ Write or Combine Your Own Post:
          </Typography>
          <TextField
            multiline
            minRows={6}
            variant="outlined"
            fullWidth
            value={customPost}
            onChange={(e) => setCustomPost(e.target.value)}
          />
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            fullWidth={isMobile}
            sx={{ mt: 2, mr: 2, borderRadius: 2, textTransform: 'none' }}
            onClick={handleSentimentCheck}
          >
            Analyze Sentiment
          </Button>
          {sentiment && (
            <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
              Sentiment: {sentiment}
            </Typography>
          )}
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth={isMobile}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
            onClick={() => {
              setError(null)
              if(customPost){
              const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(customPost)}`;
              window.open(linkedinShareUrl, '_blank');
              }
              else{
                setError("Please generate the content to make a post")
              }
            }}
          >
            Post to LinkedIn
          </Button>
        </Box>
      </Box>
    </Box>
  </>
);
};

export default LinkedInPost;
